import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosClient from '@/api/axios-client';

export type OnboardingStatus = 'not_started' | 'in_progress' | 'completed' | 'disabled';

export interface OnboardingStatusData {
    status: OnboardingStatus;
    current_step: string | null;
    current_step_index: number;
    completed_at: string | null;
    primary_admin_user_id: number | null;
    /** Au moins un flow non modèle dans l'établissement sélectionné. */
    has_flows: boolean;
}

export function useOnboardingStatus() {
    return useQuery<OnboardingStatusData>(
        ['/onboarding/status'],
        async () => (await axiosClient.get('/onboarding/status')).data,
    );
}

/**
 * Statut d'onboarding d'une facility donnée (carte super-admin).
 * Endpoint dédié avec l'ID dans l'URL : le header X-Facility-Id est ignoré
 * par SetSelectedFacility quand la facility de l'utilisateur n'est pas un groupe.
 */
export function useOnboardingStatusForFacility(facilityId: number) {
    return useQuery<OnboardingStatusData>(
        ['/onboarding/status', facilityId],
        async () =>
            (await axiosClient.get(`/super-admin/facilities/${facilityId}/onboarding/status`)).data,
    );
}

export function useAdvanceOnboarding() {
    const queryClient = useQueryClient();
    return useMutation(
        async (step: string) => (await axiosClient.patch('/onboarding/advance', { step })).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/onboarding/status']);
                queryClient.invalidateQueries(['/facilities']);
            },
        }
    );
}

/**
 * Final completion of the wizard. Called from the recap step's CTAs.
 * Marks the onboarding as completed and stamps completed_at.
 */
export function useCompleteOnboarding() {
    const queryClient = useQueryClient();
    return useMutation(
        async () => (await axiosClient.post('/onboarding/complete')).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/onboarding/status']);
                queryClient.invalidateQueries(['/facilities']);
            },
        }
    );
}

export function useSendOnboardingEmail(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async () => (await axiosClient.post(`/super-admin/facilities/${facilityId}/onboarding/send-email`)).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
            },
        }
    );
}

export function useToggleOnboarding(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async (status: 'in_progress' | 'disabled') =>
            (await axiosClient.patch(`/super-admin/facilities/${facilityId}/onboarding/toggle`, { status })).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
            },
        }
    );
}

/**
 * Reset a facility's onboarding back to "not_started".
 * Useful for super-admin testing — clears status, current_step, and completed_at.
 * Does NOT touch the user's password or must_change_password flag.
 */
export function useResetOnboarding(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async () => (await axiosClient.post(`/super-admin/facilities/${facilityId}/onboarding/reset`)).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
            },
        }
    );
}

/**
 * Rejoue l'onboarding : réinitialise l'assistant ET renvoie l'accès (nouveau
 * mot de passe temporaire), donc l'étape mot de passe est rejouée aussi.
 */
export function useReplayOnboarding(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async () => (await axiosClient.post(`/super-admin/facilities/${facilityId}/onboarding/replay`)).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
            },
        }
    );
}

/** Établissement de test remis à l'état de sa création + nouvel accès (adresse au choix). */
export function useResetTestFacility(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async (email: string | null) =>
            (await axiosClient.post(`/super-admin/facilities/${facilityId}/onboarding/reset-test`, { email })).data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
                queryClient.invalidateQueries(['/onboarding/primary-admin-candidates', facilityId]);
            },
        }
    );
}

export interface PrimaryAdminCandidate {
    id: number;
    name: string;
    email: string;
}

/** Administrateurs de l'établissement qui peuvent être désignés admin principal. */
export function usePrimaryAdminCandidates(facilityId: number) {
    return useQuery<PrimaryAdminCandidate[]>(
        ['/onboarding/primary-admin-candidates', facilityId],
        async () =>
            (await axiosClient.get(`/super-admin/facilities/${facilityId}/onboarding/primary-admin-candidates`)).data
                .data,
    );
}

export function useUpdatePrimaryAdmin(facilityId: number) {
    const queryClient = useQueryClient();
    return useMutation(
        async (userId: number) =>
            (await axiosClient.patch(`/super-admin/facilities/${facilityId}/onboarding/primary-admin`, { user_id: userId }))
                .data,
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['/facilities', facilityId]);
                queryClient.invalidateQueries(['/onboarding/status', facilityId]);
            },
        }
    );
}
